import { Grid } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import StreamFailed from "../Wowza/utils/StreamFailed";
import { CircularProgress } from "@mui/material";
import EventBus from "orion-components/SharedComponents/EventBus";
import utils from "./utils";

// Standard playback speeds supported by Vicads player
const STANDARD_SPEED_RANGE = [-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16];

let componentKey = 0;

const VicadsRecordedWebRTCPlayerWrapper = ({ camera, instanceId, containerRef, isInFullScreen, updateDimensions }) => {
	const [videoStartDateTime, setVideoStartDateTime] = useState(null);
	const [videoEndDateTime, setVideoEndDateTime] = useState(null);
	const [recordedMode, setRecordedMode] = useState(null);
	const [urlReady, setUrlReady] = useState(false);
	const [key, setKey] = useState(0);

	const setPlayBackTimeFrame = value => {
		if (value.instanceId !== instanceId) {
			// The message is for a different recorded player instance
			return;
		}
		const { startDateTimeVal, endDateTimeVal, recordedMode } = value;
		setUrlReady(false);
		setVideoStartDateTime(startDateTimeVal);
		setVideoEndDateTime(endDateTimeVal);
		setRecordedMode(recordedMode);
		setTimeout(() => {
			setUrlReady(true);
		}, 1000);
		componentKey++;
		setKey(componentKey);
	};

	const renderNewComponentInstance = data => {
		if (data.instanceId !== instanceId) {
			return;
		}
		componentKey++;
		setKey(componentKey);
	};

	useEffect(() => {
		EventBus.subscribe("VicadsRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", setPlayBackTimeFrame);
		EventBus.subscribe("VicadsRecordedWebRTCPlayerWrapper_renderNewComponentInstance", renderNewComponentInstance);

		return () => {
			EventBus.unsubscribe("VicadsRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", setPlayBackTimeFrame);
			EventBus.unsubscribe(
				"VicadsRecordedWebRTCPlayerWrapper_renderNewComponentInstance",
				renderNewComponentInstance
			);
		};
	}, []);

	if (videoStartDateTime !== null && videoEndDateTime !== null && recordedMode && urlReady) {
		return (
			<VicadsRecordedWebRTCPlayer
				key={`${key}-${camera.id}-${isInFullScreen}`}
				camera={camera}
				instanceId={instanceId}
				containerRef={containerRef}
				isInFullScreen={isInFullScreen}
				videoStartDateTime={videoStartDateTime}
				videoEndDateTime={videoEndDateTime}
				recordedMode={recordedMode}
				updateDimensions={updateDimensions}
			/>
		);
	} else {
		return (
			<Grid container spacing={0} className="mt-3">
				<div
					style={{
						height: "100%",
						width: "100%",
						padding: "2.5%",
						textAlign: "center"
					}}
				>
					<CircularProgress />
				</div>
			</Grid>
		);
	}
};

const VicadsRecordedWebRTCPlayer = ({
	camera,
	instanceId,
	containerRef,
	isInFullScreen,
	videoStartDateTime,
	videoEndDateTime,
	recordedMode,
	updateDimensions
}) => {
	const videoElemRef = useRef(null);
	const playPendingRef = useRef(true);
	const currentSpeedRef = useRef(1);
	const [token, setToken] = useState("");
	const [videoWidth, setVideoWidth] = useState(0);
	const [videoHeight, setVideoHeight] = useState(0);
	const isNewVideoPlayer = camera.player?.newPlayer || false;
	const [isLoaded, setIsLoaded] = useState(false);
	const [streamStatus, setStreamStatus] = useState("Starting");
	const [vicadsHost, setVicadsHost] = useState("");

	const clipTimerRef = useRef(null);

	useEffect(() => {
		if (camera?.player?.host) {
			setVicadsHost(camera?.player?.host);
		}
	}, [camera]);

	// The vicads-scrubber's width is set to the window's width in full screen mode. The height is calculated based on the width
	// to maintain a 16:9 aspect ratio. This prevents blank areas around the video player.
	// Vicads-video displays a black border around the video if we don't use an aspect ratio for width and height.

	useEffect(() => {
		const setAuthToken = authToken => {
			setToken(authToken);
			if (authToken === "") {
				setIsLoaded(false);
				setStreamStatus("Failed");
				return;
			}
			videoElemRef.current.vicadsauth = authToken;
		};
		utils.registerTokenSetter(instanceId, camera, setAuthToken);

		EventBus.subscribe("VicadsRecordedWebRTCPlayer_applyPlaybackCommand", applyPlaybackCommand);

		return () => {
			utils.unregisterTokenSetter(instanceId, camera);
			EventBus.unsubscribe("VicadsRecordedWebRTCPlayer_applyPlaybackCommand", applyPlaybackCommand);
			clearClipTimer();
		};
	}, []);

	useEffect(() => {
		if (isInFullScreen) {
			let dimensions = null;
			dimensions = utils.get16by9Dimensions(window.innerWidth, window.innerHeight);
			if (dimensions) {
				setVideoWidth(dimensions.width);
				setVideoHeight(dimensions.height);
			}
		}
	}, [isInFullScreen]);

	useEffect(() => {
		setVideoWidth(containerRef.current?.offsetWidth);
		setVideoHeight(containerRef.current?.offsetHeight);
		minimizedVideoViewSettings();
	}, [updateDimensions, containerRef]);

	const minimizedVideoViewSettings = () => {
		if (!isInFullScreen) {
			if (videoElemRef.current !== null && (containerRef.current || (videoWidth && videoHeight))) {
				videoElemRef.current.videowidth = containerRef.current?.offsetWidth || videoWidth;
				videoElemRef.current.videoheight = containerRef.current?.offsetHeight || videoHeight;
			}
		}
	};

	//The following useEffect hook is responsible for shrinking the video player without affecting the video quality set in the previous useEffect. In essence, if the video player is not in fullscreen mode, the container's width and height passed to this component will be used. Otherwise, the window's dimensions will be used.
	useEffect(() => {
		if (isInFullScreen) {
			if (videoElemRef.current !== null) {
				const dimensions = utils.get16by9Dimensions(window.innerWidth, window.innerHeight);
				if (dimensions) {
					videoElemRef.current.videowidth = dimensions.width;
					videoElemRef.current.videoheight = dimensions.height;
				}
			}
		} else {
			minimizedVideoViewSettings();
		}
	}, [containerRef, videoElemRef, isInFullScreen]);

	useEffect(() => {
		if (playPendingRef.current && token && videoWidth && videoHeight && recordedMode) {
			if (videoElemRef.current !== null) {
				videoElemRef.current.videowidth = videoWidth;
				videoElemRef.current.videoheight = videoHeight;
			}
			playVideo();
			playPendingRef.current = false;
		}
	}, [token, videoWidth, videoHeight, recordedMode]);

	const applyPlaybackSpeed = (videoElem, speed) => {
		const setupSpeedFromScratch = (videoElem, speedToSet) => {
			const direction = speedToSet < 0 ? -1 : 1;
			const command = speedToSet < 0 ? "reverse" : "forward";

			if (speedToSet * direction === 1) {
				videoElem.controlvideo = command;
			} else {
				videoElem.controlvideo = "stop"; // clear the previous speed
				let factor = direction * speedToSet;
				while (factor > 1) {
					videoElem.controlvideo = `fast${command}`; // either fastforward or fastreverse
					factor = factor / 2;
					if (factor > 1) {
						videoElem.controlvideo = ""; // VICADS nuance: need to clear the command before firing the same command again
					}
				}
			}
		};

		let newSpeed, oldSpeed;
		if (speed === undefined) {
			// We simply want to resume (play) with previous speed
			newSpeed = currentSpeedRef.current;
		} else {
			newSpeed = speed;
			oldSpeed = currentSpeedRef.current;
		}

		if (newSpeed > 0) {
			if (oldSpeed === undefined || oldSpeed < 0 || oldSpeed > newSpeed) {
				// We set new speed fresh from scratch
				setupSpeedFromScratch(videoElem, newSpeed);
			} else if (oldSpeed < newSpeed) {
				// We always expect a single increment
				videoElem.controlvideo = "";
				videoElem.controlvideo = "fastforward";
			}
		} else {
			if (oldSpeed === undefined || oldSpeed > 0 || oldSpeed < newSpeed) {
				// We set new speed fresh from scratch
				setupSpeedFromScratch(videoElem, newSpeed);
			} else if (oldSpeed > newSpeed) {
				// We always expect a single decrement
				videoElem.controlvideo = "";
				videoElem.controlvideo = "fastreverse";
			}
		}
		currentSpeedRef.current = newSpeed;
	};

	const applyPlaybackCommand = commandObj => {
		if (commandObj.instanceId !== instanceId) {
			return;
		}
		const videoElem = videoElemRef.current;
		if (!videoElem) return;
		switch (commandObj.command) {
			case "pause":
				console.log("Applying recorded video command: pause");
				videoElem.controlvideo = "stop";
				break;
			case "play":
				console.log("Applying recorded video command: play");
				applyPlaybackSpeed(videoElem);
				break;
			case "setCursor":
				console.log(`Applying recorded video command: setCursor (${commandObj.time})`);
				videoElem.cursor = commandObj.time.getTime();
				// The VICADS scrubber does not raise the timestamp event when cursor is set programmatically.
				EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: commandObj.time });
				break;
			case "setSpeed":
				console.log(`Applying recorded video command: setSpeed (${commandObj.speed})`);
				applyPlaybackSpeed(videoElem, commandObj.speed);
				break;
		}
	};

	const startClipTimer = () => {
		const durationInSeconds = (videoEndDateTime - videoStartDateTime) / 1000;
		let startingTime = moment();
		clipTimerRef.current = setInterval(() => {
			const elapsedTime = moment().diff(startingTime, "seconds");
			const elapsedTimeInRange = elapsedTime % durationInSeconds;
			const newTime = moment(videoStartDateTime).add(elapsedTimeInRange, "seconds").toDate();
			EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: newTime });
		}, 1000);
	};

	const getPlayerInfoHandler = event => {
		console.log("Video player Info:", event.detail);
		if (
			(recordedMode === "recorded" && event.detail === "Thumbnail replay started...") ||
			(recordedMode === "clip" && event.detail === "playing")
		) {
			setIsLoaded(true);
			setStreamStatus("Success");
			minimizedVideoViewSettings();
			if (recordedMode === "recorded") {
				const videoElem = videoElemRef.current;
				if (!videoElem) return;
				videoElem.controlvideo = "forward";
			} else {
				startClipTimer();
			}
			EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: true });
			// Publish player capabilities after RecordedBottomBar is ready
			console.debug(
				`[VicadsRecordedWebRTCPlayer][${instanceId}] Publishing capabilities (getPlayerInfoHandler):`,
				STANDARD_SPEED_RANGE
			);
			EventBus.publish("VideoPlayer_CapabilitiesReady", {
				instanceId,
				supportedSpeeds: STANDARD_SPEED_RANGE
			});
		}
		if (event.detail === "WebSocket error" || event.detail.includes("Viewing error")) {
			//handle loaders and fall back
			setIsLoaded(false);
			setStreamStatus("Failed");
			clearClipTimer();
		}
	};

	const getPlayerErrorHandler = event => {
		console.log("Error:", event.detail);
		clearClipTimer();
	};

	const getPlayerTimestampHandler = event => {
		if (!isNaN(event.detail)) {
			EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: new Date(event.detail) });
		} else {
			console.log("Timestamp returned as NaN by scrubber");
		}
	};

	const getPlayerLoadProgressHandler = event => {
		EventBus.publish("RecordedBottomBar_loadProgress", { instanceId, progress: event.detail });
	};

	// This useEffect is responsible for listening to changes in the video player.
	// If the video starts streaming successfully, it will stop the loader and display the actual video content.
	// If not, an error message with a retry button will be displayed on the player.
	// It also publishes load progress and current video timestamp to the recorded bottom bar.
	useEffect(() => {
		const videoElement = videoElemRef.current;

		if (videoElement) {
			videoElement.addEventListener("info", getPlayerInfoHandler);
			videoElement.addEventListener("error", getPlayerErrorHandler);
			if (recordedMode === "recorded") {
				videoElement.addEventListener("timestamp", getPlayerTimestampHandler);
				videoElement.addEventListener("loadprogress", getPlayerLoadProgressHandler);
			}
		}
		return () => {
			// Remove event listeners when the component unmounts
			videoElement.removeEventListener("info", getPlayerInfoHandler);
			videoElement.removeEventListener("error", getPlayerErrorHandler);
			if (recordedMode === "recorded") {
				videoElement.removeEventListener("timestamp", getPlayerTimestampHandler);
				videoElement.removeEventListener("loadprogress", getPlayerLoadProgressHandler);
			}
		};
	}, [videoElemRef, recordedMode]);

	const clearClipTimer = () => {
		if (clipTimerRef.current) {
			clearInterval(clipTimerRef.current);
			clipTimerRef.current = null;
		}
	};

	const clearVideo = () => {
		const videoElem = videoElemRef.current;
		if (!videoElem) return;
		videoElem.playvideo = "";
		clearClipTimer();
	};

	// This function is called when the vicads-scrubber element gets an auth property during login.
	// This sets the playvideo property of vicads-scrubber with the captured video ID, initiating WebSocket
	// and other background processes to stream the video.
	const playVideo = () => {
		const videoElem = videoElemRef.current;
		if (!videoElem) return;
		const videoId = camera.connection.cameraId;
		let command = "scrub";
		let additionalParams = "";
		if (recordedMode === "clip") {
			command = "recorded";
			additionalParams = "&loopVideo=true";
		}
		videoElem.playvideo = `${command}?videoUID=${videoId}&fromTimestamp=${videoStartDateTime}&toTimestamp=${videoEndDateTime}${additionalParams}`;
	};

	const retryStream = () => {
		clearVideo();
		setTimeout(() => {
			playVideo();
			setIsLoaded(false);
			setStreamStatus("Starting");
		}, 500);
	};

	//  This function is used to display a loader initially until the WebSocket connection completes and starts streaming video.
	//  If there is an issue with the connection, it will display a fallback message on the video player,
	// allowing us to retry connecting to the WebSocket.
	const streamFallbackHandler = () => {
		if (streamStatus === "Starting") {
			return (
				<div
					style={{
						height: "100%",
						width: "100%",
						padding: "2.5%",
						textAlign: "center"
					}}
				>
					<CircularProgress />
				</div>
			);
		} else if (streamStatus === "Failed") {
			return <StreamFailed retryStream={retryStream} />;
		}

		return null;
	};

	return (
		<Grid
			container
			spacing={isNewVideoPlayer ? 0 : 3}
			sx={{ ...(isLoaded && { width: "100%", height: "100%" }) }}
			className="mt-3"
		>
			{!isLoaded && streamFallbackHandler()}
			<div
				style={{
					display: isLoaded && vicadsHost !== "" ? "flex" : "none",
					width: "100%",
					height: "100%",
					justifyContent: "center"
				}}
			>
				{/* The vicads-scrubber and vicads-video elements are from the vicads main.js file fetched from MinIO.*/}
				{recordedMode === "recorded" && (
					<vicads-scrubber
						id={"VicadsVideoPlayer" + instanceId}
						vicadsHost={vicadsHost}
						showcontrols={false}
						showtimeline={false}
						viewerconfig='{ "minBandwidth": 4096, "maxBandwidth": 12288 }'
						ref={videoElemRef}
					></vicads-scrubber>
				)}
				{recordedMode === "clip" && (
					<vicads-video
						id={"VicadsVideoPlayer" + instanceId}
						videoTag={"video" + instanceId}
						vicadsHost={vicadsHost}
						viewerconfig='{ "minBandwidth": 4096, "maxBandwidth": 12288 }'
						ref={videoElemRef}
					></vicads-video>
				)}
			</div>
		</Grid>
	);
};

export default VicadsRecordedWebRTCPlayerWrapper;
